import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, IndianRupee, ArrowUpRight, ArrowDownRight,
  Calendar, Search
} from 'lucide-react';
import MemberLayout from '../../components/layouts/MemberLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';

const MemberPassbook = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await apiClient.get('/dashboard/member-stats');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch passbook data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'contribution':
        return <ArrowDownRight className="w-5 h-5 text-green-600" />;
      case 'loan_disbursement':
        return <ArrowUpRight className="w-5 h-5 text-blue-600" />;
      case 'emi_payment':
        return <IndianRupee className="w-5 h-5 text-purple-600" />;
      default:
        return <IndianRupee className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTransactionStyle = (type) => {
    switch (type) {
      case 'contribution':
        return 'bg-green-100';
      case 'loan_disbursement':
        return 'bg-blue-100';
      case 'emi_payment':
        return 'bg-purple-100';
      case 'penalty':
        return 'bg-red-100';
      case 'interest':
        return 'bg-orange-100';
      default:
        return 'bg-gray-100';
    }
  };

  const filteredTransactions = data?.transactions?.filter(tx =>
    tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.type.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Calculate running balance
  let runningBalance = 0;
  const transactionsWithBalance = [...filteredTransactions].reverse().map(tx => {
    if (['contribution'].includes(tx.type)) {
      runningBalance += tx.amount;
    } else if (tx.type === 'loan_disbursement') {
      runningBalance -= tx.amount;
    }
    return { ...tx, balance: runningBalance };
  }).reverse();

  return (
    <MemberLayout title="Digital Passbook" subtitle="Complete transaction history">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0A2540]/5 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#0A2540]" />
            </div>
            <div>
              <p className="text-sm text-[#425466]">Total Transactions</p>
              <p className="text-2xl font-bold text-[#0A2540]">{data?.transactions?.length || 0}</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <ArrowDownRight className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-[#425466]">Total Deposits</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(data?.total_contributions || 0)}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00C853]/10 flex items-center justify-center">
              <IndianRupee className="w-6 h-6 text-[#00C853]" />
            </div>
            <div>
              <p className="text-sm text-[#425466]">Current Balance</p>
              <p className="text-2xl font-bold text-[#00C853]">
                {formatCurrency(data?.total_capital || 0)}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
          <Input
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="passbook-search-input"
          />
        </div>
      </div>

      {/* Passbook Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F7FB]">
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase tracking-wider">Description</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase tracking-wider">Debit</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase tracking-wider">Credit</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase tracking-wider font-mono">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded-full"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-48 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                  </tr>
                ))
              ) : transactionsWithBalance.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-[#425466]">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No transactions found</p>
                  </td>
                </tr>
              ) : (
                transactionsWithBalance.map((tx, index) => (
                  <tr 
                    key={tx.id} 
                    className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#425466]" />
                        <div>
                          <p className="text-sm font-medium text-[#0A2540]">
                            {new Date(tx.created_at).toLocaleDateString('en-IN')}
                          </p>
                          <p className="text-xs text-[#425466]">
                            {new Date(tx.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${getTransactionStyle(tx.type)} flex items-center justify-center`}>
                          {getTransactionIcon(tx.type)}
                        </div>
                        <span className="text-sm capitalize text-[#425466]">{tx.type.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#0A2540]">{tx.description}</p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {tx.type === 'loan_disbursement' ? (
                        <span className="text-red-600 font-semibold">{formatCurrency(tx.amount)}</span>
                      ) : (
                        <span className="text-[#425466]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {['contribution', 'emi_payment', 'interest'].includes(tx.type) ? (
                        <span className="text-green-600 font-semibold">{formatCurrency(tx.amount)}</span>
                      ) : (
                        <span className="text-[#425466]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-mono font-semibold text-[#0A2540]">
                        {formatCurrency(tx.balance_after || data?.total_capital || 0)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary Footer */}
      <div className="mt-6 text-center text-sm text-[#425466]">
        Showing {filteredTransactions.length} transactions
      </div>
    </MemberLayout>
  );
};

export default MemberPassbook;
